import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { BookOpen, Download, ExternalLink, Copy, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Read-only dialog showing the exact procedure to export historical sales emails
 * (reportingservices@gbh.fr) from a third party Gmail account into a `.mbox` file
 * — ready to be imported into Sokuzai.
 *
 * Triggered by a button placed in the Imports tab of the Sales page.
 * Source of truth: /app/memory/PINNED_mbox_import_plan.md
 */
export default function MboxExportGuideDialog({ open, onOpenChange }) {
    const gmailSearch = 'from:reportingservices@gbh.fr';

    const copyToClipboard = (text, label) => {
        navigator.clipboard?.writeText(text);
        toast.success(`${label} copié dans le presse-papier`);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="mbox-export-guide-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <BookOpen className="w-5 h-5 text-blue-500" />
                        Exporter l’historique des mails « reportingservices »
                    </DialogTitle>
                    <DialogDescription>
                        Procédure pour récupérer un large historique de rapports de vente depuis un compte Gmail tiers
                        et les importer dans Sokuzai au format <code className="text-xs bg-slate-100 px-1 rounded">.mbox</code>.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 text-sm">
                    {/* Step 1 */}
                    <section className="border-l-4 border-blue-400 pl-3 py-1">
                        <h3 className="font-semibold text-blue-700 mb-1">1. Créer un libellé Gmail</h3>
                        <p className="text-slate-600">
                            Dans Gmail, créer un nouveau libellé nommé exactement :
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                            <Badge className="bg-amber-50 text-amber-800 border border-amber-200 font-mono text-xs">EXPORT_VENTES_HISTO</Badge>
                            <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => copyToClipboard('EXPORT_VENTES_HISTO', 'Libellé')} data-testid="copy-label-btn">
                                <Copy className="w-3 h-3" />
                            </Button>
                        </div>
                    </section>

                    {/* Step 2 */}
                    <section className="border-l-4 border-blue-400 pl-3 py-1">
                        <h3 className="font-semibold text-blue-700 mb-1">2. Étiqueter tous les mails concernés</h3>
                        <p className="text-slate-600 mb-1">Dans la barre de recherche Gmail, coller :</p>
                        <div className="flex items-center gap-2">
                            <code className="bg-slate-100 px-2 py-1 rounded text-xs font-mono flex-1">{gmailSearch}</code>
                            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => copyToClipboard(gmailSearch, 'Recherche')} data-testid="copy-search-btn">
                                <Copy className="w-3 h-3" />
                            </Button>
                        </div>
                        <ul className="list-disc list-inside text-xs text-slate-500 mt-2 space-y-0.5">
                            <li>Cliquer la case en haut à gauche → <em>Tout sélectionner</em></li>
                            <li>Si une bannière apparaît : <em>« Sélectionner les XXXX conversations »</em></li>
                            <li>Bouton <strong>Libellés</strong> → cocher <code className="text-xs">EXPORT_VENTES_HISTO</code> → <em>Appliquer</em></li>
                        </ul>
                    </section>

                    {/* Step 3 */}
                    <section className="border-l-4 border-blue-400 pl-3 py-1">
                        <h3 className="font-semibold text-blue-700 mb-1">3. Lancer l’export Google Takeout</h3>
                        <a
                            href="https://takeout.google.com"
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 text-blue-600 hover:underline text-xs mb-1"
                            data-testid="takeout-link"
                        >
                            <ExternalLink className="w-3 h-3" />
                            takeout.google.com
                        </a>
                        <ol className="list-decimal list-inside text-xs text-slate-600 space-y-0.5 mt-1">
                            <li>Cliquer <strong>« Tout désélectionner »</strong></li>
                            <li>Cocher uniquement <strong>Gmail</strong></li>
                            <li>Cliquer <strong>« Tous les messages »</strong> → décocher tout → cocher uniquement le libellé <code className="text-xs">EXPORT_VENTES_HISTO</code> → OK</li>
                            <li>Format : <strong>MBOX</strong> (par défaut)</li>
                            <li>Étape suivante → <strong>« Créer l’exportation »</strong></li>
                            <li>Google envoie un lien par mail dans quelques minutes/heures avec un ou plusieurs fichiers <code className="text-xs">.mbox</code></li>
                        </ol>
                    </section>

                    {/* Step 4 */}
                    <section className="border-l-4 border-emerald-400 pl-3 py-1">
                        <h3 className="font-semibold text-emerald-700 mb-1">4. Transmettre le(s) fichier(s)</h3>
                        <p className="text-xs text-slate-600">
                            Envoyer le ou les <code className="text-xs">.mbox</code> via <strong>WeTransfer</strong>, <strong>Google Drive</strong>
                            ou tout autre service de transfert de gros fichiers. Préciser l’année couverte si plusieurs envois.
                        </p>
                    </section>

                    {/* Outlook / PST */}
                    <section className="border-l-4 border-slate-300 pl-3 py-1">
                        <h3 className="font-semibold text-slate-700 mb-1 text-xs uppercase">Cas Outlook / fichier .pst</h3>
                        <p className="text-xs text-slate-600">
                            Si la messagerie source est Outlook : ouvrir le <code className="text-xs">.pst</code> dans <strong>Thunderbird</strong>,
                            puis exporter le dossier filtré au format <code className="text-xs">.mbox</code> via l’extension « ImportExportTools NG ».
                            Même flux ensuite.
                        </p>
                    </section>

                    {/* Précautions */}
                    <section className="bg-amber-50 border border-amber-200 rounded p-3">
                        <h3 className="font-semibold text-amber-800 mb-1 text-xs uppercase flex items-center gap-1">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Bonne pratique
                        </h3>
                        <p className="text-xs text-amber-900">
                            Si l’historique remonte avant <strong>2022</strong>, demander d’abord <strong>1 ou 2 mails de test</strong>
                            (au format <code className="text-xs">.eml</code> ou en pièce jointe) pour vérifier que le parser Excel actuel
                            accepte les formats anciens, <em>avant</em> de lancer le gros export.
                        </p>
                    </section>

                    {/* Bouton import (placeholder pour quand le endpoint sera prêt) */}
                    <section className="bg-slate-50 border border-slate-200 rounded p-3 text-center">
                        <p className="text-xs text-slate-500 mb-2">
                            Une fois le fichier <code className="text-xs">.mbox</code> récupéré, l’import dans Sokuzai se fera via un bouton dédié
                            (à implémenter dans cet onglet).
                        </p>
                        <Badge variant="outline" className="text-[10px] uppercase">Implémentation à venir</Badge>
                    </section>
                </div>

                <DialogFooter className="pt-2 border-t">
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="close-mbox-guide-btn">
                        Fermer
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/**
 * Toggle button to open the export guide. Place it inside the Imports tab of the Sales page.
 */
export function MboxExportGuideButton({ className = '' }) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <Button
                size="sm"
                variant="outline"
                className={`h-8 border-blue-300 text-blue-700 hover:bg-blue-50 ${className}`}
                onClick={() => setOpen(true)}
                data-testid="open-mbox-guide-btn"
                title="Procédure d’export des mails historiques reportingservices"
            >
                <Download className="w-3.5 h-3.5 mr-1" />
                Procédure d’export historique
            </Button>
            <MboxExportGuideDialog open={open} onOpenChange={setOpen} />
        </>
    );
}
